import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Plus } from "lucide-react";
import api, { eur, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { PageHeader } from "@/components/ui-bits";
import { useLang } from "@/lib/i18n";

export const Checkout = () => {
  const navigate = useNavigate();
  const { t, pick } = useLang();
  const { user, refreshUser } = useAuth();
  const { items, subtotal, coupon, shipping, setShipping, clear } = useCart();
  const SHIPPING_OPTIONS = [
    ["standard", t("checkout.shipStandard"), t("checkout.shipStdDays"), 3.95],
    ["express", t("checkout.shipExpress"), t("checkout.shipExpDays"), 7.95],
  ];
  const EMPTY_ADDRESS = { label: t("checkout.homeLabel"), name: "", street: "", house_number: "", postal_code: "", city: "", country: "Nederland", phone: "", is_default: false };
  const [addressId, setAddressId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_ADDRESS);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const addresses = user?.addresses || [];
  const discount = coupon?.discount || 0;
  const freeShip = subtotal - discount >= 50;
  const shipCost = freeShip ? 0 : (SHIPPING_OPTIONS.find((s) => s[0] === shipping)?.[3] || 0);
  const total = Math.max(0, subtotal - discount + shipCost);

  useEffect(() => {
    if (addresses.length && !addressId) {
      const def = addresses.find((a) => a.is_default) || addresses[0];
      setAddressId(def.id);
    }
  }, [addresses]);

  useEffect(() => {
    if (items.length === 0 && !window.__deo_checkout_started) {
      const t = setTimeout(() => navigate("/winkelwagen"), 1500);
      return () => clearTimeout(t);
    }
  }, [items.length]);

  const submitAddress = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/auth/addresses", { ...form, is_default: addresses.length === 0 });
      await refreshUser();
      setShowForm(false);
      setForm(EMPTY_ADDRESS);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const pay = async () => {
    setError("");
    setBusy(true);
    try {
      const d = await api.post("/checkout", {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        address_id: addressId,
        shipping_method: shipping,
        coupon_code: coupon?.code || "",
      });
      window.__deo_checkout_started = true;
      window.location.href = d.data.checkout_url;
    } catch (err) {
      setError(formatApiError(err));
      setBusy(false);
    }
  };

  const field = (key, label, testid, required = true) => (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke block mb-2">{label}</span>
      <input
        data-testid={testid}
        required={required}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full bg-obsidian border border-gold/15 px-4 py-3 text-sm text-cream focus:outline-none focus:border-gold/50"
      />
    </label>
  );

  if (items.length === 0 && !window.__deo_checkout_started) {
    return (
      <div className="px-6 sm:px-12 lg:px-20 pt-32 pb-24">
        <p className="text-sm text-mist">{t("checkout.emptyRedirect")}</p>
      </div>
    );
  }

  return (
    <div className="px-6 sm:px-12 lg:px-20 pt-32 pb-24">
      <PageHeader eyebrow={t("checkout.eyebrow")} title={<>{t("checkout.title1")} <span className="italic normal-case text-gold">{t("checkout.title2")}</span></>} />

      <div className="grid lg:grid-cols-[2fr_1fr] gap-12">
        <div className="space-y-12">
          <section>
            <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/85 mb-5">{t("checkout.address")}</h2>
            <div className="space-y-3">
              {addresses.map((a) => (
                <button
                  key={a.id}
                  data-testid={`address-option-${a.id}`}
                  onClick={() => setAddressId(a.id)}
                  className={`w-full text-left border p-5 transition-colors duration-300 ${addressId === a.id ? "border-gold bg-gold/5" : "border-cream/10 bg-ink hover:border-gold/40"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-gold/80">{a.label}{a.is_default ? ` · ${t("checkout.default")}` : ""}</span>
                    {addressId === a.id && <span className="h-2 w-2 rounded-full bg-gold" />}
                  </div>
                  <p className="mt-2 text-sm text-cream">{a.name} — {a.street} {a.house_number}, {a.postal_code} {a.city}, {a.country}</p>
                </button>
              ))}
              {!showForm && (
                <button data-testid="new-address-toggle" onClick={() => setShowForm(true)} className="flex items-center gap-2 border border-dashed border-gold/30 text-gold font-mono text-[10px] tracking-[0.25em] uppercase px-5 py-4 w-full justify-center hover:bg-gold/5 transition-colors">
                  <Plus size={14} /> {t("checkout.newAddress")}
                </button>
              )}
              {showForm && (
                <form data-testid="new-address-form" onSubmit={submitAddress} className="border border-gold/20 bg-ink p-6 grid sm:grid-cols-2 gap-4">
                  {field("name", t("checkout.fields.name"), "address-name-input")}
                  {field("phone", t("checkout.fields.phone"), "address-phone-input", false)}
                  {field("street", t("checkout.fields.street"), "address-street-input")}
                  {field("house_number", t("checkout.fields.number"), "address-number-input")}
                  {field("postal_code", t("checkout.fields.postal"), "address-postal-input")}
                  {field("city", t("checkout.fields.city"), "address-city-input")}
                  <div className="sm:col-span-2 flex gap-3">
                    <button type="submit" data-testid="address-save-button" className="bg-gold text-obsidian font-mono text-[10px] tracking-[0.3em] uppercase px-8 py-3.5">{t("checkout.fields.save")}</button>
                    <button type="button" onClick={() => setShowForm(false)} className="border border-cream/15 text-mist font-mono text-[10px] tracking-[0.25em] uppercase px-8 py-3.5">{t("checkout.fields.cancel")}</button>
                  </div>
                </form>
              )}
            </div>
          </section>

          <section>
            <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/85 mb-5">{t("checkout.shipping")}</h2>
            <div className="space-y-3">
              {SHIPPING_OPTIONS.map(([key, label, days, cost]) => (
                <button
                  key={key}
                  data-testid={`shipping-option-${key}`}
                  onClick={() => setShipping(key)}
                  className={`w-full flex items-center justify-between border p-5 transition-colors duration-300 ${shipping === key ? "border-gold bg-gold/5" : "border-cream/10 bg-ink hover:border-gold/40"}`}
                >
                  <div className="text-left">
                    <p className="text-sm text-cream">{label}</p>
                    <p className="font-mono text-[10px] text-smoke mt-1 uppercase tracking-[0.2em]">{days}</p>
                  </div>
                  <p className="font-serif text-lg text-gold">{freeShip && key === "standard" ? t("checkout.free") : eur(cost)}</p>
                </button>
              ))}
              {freeShip && <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-emerald-300/90">{t("checkout.freeUnlocked")}</p>}
            </div>
          </section>
        </div>

        <div className="h-fit lg:sticky lg:top-28 border border-gold/20 bg-elevate p-8">
          <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/85 mb-6">{t("checkout.order")}</h2>
          <div className="space-y-4 max-h-64 overflow-y-auto pr-1">
            {items.map((i) => (
              <div key={i.slug} className="flex items-center gap-3 text-sm">
                <div className={`w-12 h-14 shrink-0 ${i.image_style === "studio" ? "bg-[#ECEAE3]" : "bg-ink"}`}>
                  <img src={i.image_url} alt="" className={`w-full h-full ${i.image_style === "studio" ? "object-contain p-1" : "object-cover"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-cream truncate">{pick(i, "model")}</p>
                  <p className="font-mono text-[10px] text-smoke">{i.quantity} × {eur(i.discount_price || i.price)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-3 border-t border-gold/10 pt-5 mt-5 text-sm">
            <div className="flex justify-between text-mist"><span>{t("cart.subtotal")}</span><span>{eur(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-gold"><span>{t("cart.discount")} ({coupon.code})</span><span>−{eur(discount)}</span></div>}
            <div className="flex justify-between text-mist"><span>{t("account.shipMethod")}</span><span>{freeShip ? t("checkout.free") : eur(shipCost)}</span></div>
            <div className="flex justify-between items-baseline border-t border-gold/10 pt-4">
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">{t("checkout.total")}</span>
              <span data-testid="checkout-total" className="font-serif text-2xl text-gold">{eur(total)}</span>
            </div>
          </div>
          {error && <p data-testid="checkout-error" className="mt-4 text-xs text-red-400">{error}</p>}
          <button
            data-testid="pay-button"
            onClick={pay}
            disabled={busy || !addressId}
            className="w-full mt-7 flex items-center justify-center gap-3 bg-gold text-obsidian font-mono text-xs tracking-[0.3em] uppercase py-4 hover:bg-gold-light transition-colors disabled:opacity-40"
          >
            <CreditCard size={15} /> {busy ? t("checkout.connecting") : `${t("checkout.pay")} ${eur(total)}`}
          </button>
          <p className="mt-4 font-mono text-[9px] tracking-[0.2em] uppercase text-smoke text-center leading-relaxed">
            {t("checkout.secure")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
